import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { SignUpForm } from "./components/SignUpForm";
import { SignUpSchema, type SignUpSchemaType } from "@/schemas/signUp.schema";
import { Link, useNavigate } from "@tanstack/react-router";
import { Toaster } from "@/components/ui/toast/toaster";
import { t } from "i18next";
import { SignHeader } from "@/components/ui/SignHeader";
import { useCreateUser } from "@/hooks/user/useCreateUser";
import { useOccupationsStore } from "@/store/occupations.store";
import { useToast } from "@/hooks/use-toast";

const initialValue: SignUpSchemaType = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
  acceptTerms: false,
  occupation: "",
  occupationId: 0,
};

export const SignUp = (): React.ReactNode => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { createUser, loading, error, result } = useCreateUser();
  const {
    fetchAndSetOccupations,
    loading: loadingOccupations,
    getOccupations,
  } = useOccupationsStore();

  const form = useForm<SignUpSchemaType>({
    resolver: zodResolver(SignUpSchema()),
    shouldUnregister: false,
    reValidateMode: "onBlur",
    defaultValues: initialValue,
  });

  useEffect(() => {
    void fetchAndSetOccupations();
  }, [fetchAndSetOccupations]);

  useEffect(() => {
    if (result) {
      form.reset();
      toast({
        variant: "success",
        title: t("signUp.form.messages.accountCreated.title"),
        description: t("signUp.form.messages.accountCreated.description"),
      });
      void setTimeout(() => {
        void navigate({ to: "/" });
      }, 3000);
    }
    if (error) {
      form.setError("root", {
        type: "manual",
        message: error,
      });
    }
  }, [error, result, form, navigate, toast]);

  const handleSubmit = async (data: SignUpSchemaType): Promise<void> => {
    await createUser({
      email: data.email,
      firstname: data.firstName,
      lastname: data.lastName,
      password: data.password,
      occupationId: data.occupationId,
    });
  };

  return (
    <div className="w-full flex flex-col justify-center items-center">
      <div className="w-full flex flex-col max-w-[450px] px-4 gap-4">
        <SignHeader title={t("signUp.title")} />
        <SignUpForm
          form={form}
          isSubmitting={loading}
          loadingOccupations={loadingOccupations}
          occupations={getOccupations()}
          onSubmit={handleSubmit}
        />
        <Link className="text-center font-normal text-base" to={"/"}>
          {t("signUp.hasAccount")}{" "}
          <span className="text-primary cursor-pointer">
            {t("signUp.loginLink")}
          </span>
        </Link>
        <div className="fixed bottom-0 left-0 w-full z-50">
          <Toaster />
        </div>
      </div>
    </div>
  );
};
